const CACHE_NAME = 'geobu-media-v1';
const SHELL_CACHE_FALLBACK = 'geobu-shell-v2';

export type CacheProgressCallback = (completed: number, total: number) => void;

export class MediaCacheService {
  private cacheAvailable: boolean = typeof window !== 'undefined' && 'caches' in window;
  private pendingDownloads: Set<string> = new Set();

  async cacheMediaUrl(url: string): Promise<void> {
    if (!url || !this.cacheAvailable || this.pendingDownloads.has(url)) return;

    try {
      const cache = await caches.open(CACHE_NAME);
      const existing = await cache.match(url);
      if (existing) return;

      this.pendingDownloads.add(url);
      const response = await fetch(url, { mode: 'cors' });
      if (response.ok) {
        await cache.put(url, response.clone());
      }
    } catch (err) {
      console.warn(`Failed to pre-cache media ${url}:`, err);
    } finally {
      this.pendingDownloads.delete(url);
    }
  }

  async prefetchMediaList(urls: string[], onProgress?: CacheProgressCallback): Promise<void> {
    const validUrls = urls.filter((u) => Boolean(u));
    if (validUrls.length === 0) return;

    let completed = 0;
    const total = validUrls.length;
    onProgress?.(completed, total);

    for (const url of validUrls) {
      await this.cacheMediaUrl(url);
      completed++;
      onProgress?.(completed, total);
    }
  }

  async isAppShellPresaved(): Promise<boolean> {
    if (!this.cacheAvailable) return false;
    try {
      const matched = (await caches.match('/index.html')) || (await caches.match('/'));
      return Boolean(matched);
    } catch {
      return false;
    }
  }

  async ensureAppShellPresaved(): Promise<boolean> {
    if (!this.cacheAvailable || typeof window === 'undefined') return false;
    try {
      const allKeys = await caches.keys();
      const shellCacheName = allKeys.find((k) => k.startsWith('geobu-shell-')) || SHELL_CACHE_FALLBACK;
      const cache = await caches.open(shellCacheName);

      const resourceEntries =
        typeof performance !== 'undefined' && typeof performance.getEntriesByType === 'function'
          ? (performance.getEntriesByType('resource') as PerformanceResourceTiming[]).map((r) => r.name)
          : [];

      const urls = Array.from(
        new Set(['/', '/index.html', '/manifest.webmanifest', '/icon.svg', ...resourceEntries])
      );

      if (navigator.onLine) {
        await Promise.allSettled(
          urls.map(async (rawUrl) => {
            if (!rawUrl) return;
            const u = new URL(rawUrl, window.location.origin);
            if (u.origin !== window.location.origin) return;
            if (u.pathname.startsWith('/api/') || u.pathname.endsWith('/sw.js')) return;

            const existing = await cache.match(u.href);
            if (existing) return;

            const res = await fetch(u.href, { headers: { 'X-Geobu-Warm': '1' } });
            if (res && res.status === 200) {
              await cache.put(u.href, res.clone());
              if (u.pathname === '/' || u.pathname === '/index.html') {
                await cache.put('/', res.clone());
                await cache.put('/index.html', res.clone());
              }
            }
          })
        );
      }

      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration('/');
        reg?.active?.postMessage({ type: 'WARM_APP_SHELL', urls });
      }

      return this.isAppShellPresaved();
    } catch {
      return false;
    }
  }

  async clearCache(): Promise<void> {
    if (this.cacheAvailable) {
      await caches.delete(CACHE_NAME);
    }
  }
}

export const mediaCache = new MediaCacheService();
